import { array, boolean, object, string } from 'yup';
import { chain } from 'lodash';

import { formatForWire, isNotDefined, parseNumbers } from 'utilities/common';

import { MAX_LENGTH_ERROR_MESSAGE, REQUIRED_NUMBER } from 'modules/common/constants';
import { ZERO_GUID } from 'modules/enumerations/enumerationIds';

const occupantSchema = array().of(object().shape({
  id: REQUIRED_NUMBER,
}));

// Serializes a list of selected occupant instances to the ids the API expects,
// keeping only occupants that still exist in the current family collection and
// dropping unsaved (id 0) entries the backend could not link.
const toOccupantIds = (occupants, collection) =>
  chain(occupants)
    .filter((occupant) => !!occupant.id && collection.some((instance) => instance.id === occupant.id))
    .map('id')
    .value();

export default class TemporaryLivingInformation {
  static schema = object().shape({
    isTemporaryLivingServiceInitiated: boolean(),
    occupantContacts: occupantSchema,
    occupantPets: occupantSchema,
    specialRequestsPreferences: string().max(1000, MAX_LENGTH_ERROR_MESSAGE),
    costCap: string().max(10, MAX_LENGTH_ERROR_MESSAGE),
    bedroomsPreferred: string(),
    assignmentTypeId: string().nullable(),
  });

  constructor(wireData, contacts, pets) {
    const json = wireData || {};
    this.id = json.id;
    this.isTemporaryLivingServiceInitiated = !!json.isTemporaryLivingServiceInitiated;
    this.specialRequestsPreferences = json.specialRequestsPreferences || '';
    this.bedroomsPreferred = isNotDefined(json.bedroomsPreferred) ? '' : json.bedroomsPreferred;
    // EnumerationV2 AssignmentType id (same list as the authorization's Temp Living assignment); the empty guid means none
    this.assignmentTypeId = json.assignmentTypeId && json.assignmentTypeId !== ZERO_GUID ? json.assignmentTypeId : '';

    this.occupantPets = json.pets && pets ? pets.filter((pet) => json.pets.some((occupantId) => pet.id === occupantId)) : [];
    this.occupantContacts = json.contacts && contacts ? contacts.filter((contact) => json.contacts.some((occupantId) => contact.id === occupantId)) : [];
  }

  static fromWireData(wireData, contacts, pets) {
    return new TemporaryLivingInformation(wireData, contacts, pets);
  }

  static generateErrors(errors) {
    return errors;
  }

  isPopulated() {
    return this.specialRequestsPreferences ||
      this.bedroomsPreferred ||
      this.assignmentTypeId ||
      this.occupantContacts.length > 0 ||
      this.occupantPets.length > 0;
  }

  toWireData(contacts, pets) {
    return formatForWire({
      id: this.id,
      isTemporaryLivingServiceInitiated: this.isTemporaryLivingServiceInitiated,
      specialRequestsPreferences: this.specialRequestsPreferences,
      assignmentTypeId: this.assignmentTypeId,
      pets: toOccupantIds(this.occupantPets, pets),
      contacts: toOccupantIds(this.occupantContacts, contacts),
      ...parseNumbers({
        bedroomsPreferred: this.bedroomsPreferred,
      }),
    });
  }
}
